/**
 * AnalyticsService — Intégration Matomo
 *
 * Centralise tout le tracking analytics :
 *   - Chargement du script Matomo (lazy, une seule fois)
 *   - Page views (appelé manuellement ou via RouterTrackingService)
 *   - Events métier (vente, produit, onboarding, paiement…)
 *   - Identification utilisateur (setUserId / resetUserId)
 *
 * Approche : window._paq (API officielle Matomo) sans dépendance npm.
 * Désactivé automatiquement en environnement de développement.
 */
import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Déclaration du type global Matomo pour TypeScript
declare global {
  interface Window {
    _paq: Array<unknown[]>;
    Matomo?: unknown;
  }
}

/** Catégories d'events métier */
export const MATOMO_CATEGORIES = {
  AUTH: 'Authentification',
  VENTE: 'Vente',
  PRODUIT: 'Produit',
  ONBOARDING: 'Onboarding',
  EVENT: 'Evenement',
  ABONNEMENT: 'Abonnement',
  NAVIGATION: 'Navigation',
  SERVICE: 'Service',
  PARAMETRES: 'Parametres',
} as const;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private initialized = false;
  private readonly cfg = environment.matomo;

  // ─── Initialisation ──────────────────────────────────────────────────────

  /**
   * Charge le script Matomo et configure le tracker.
   * À appeler une seule fois dans app.config.ts via APP_INITIALIZER.
   */
  init(): void {
    if (
      !this.cfg.enabled ||
      this.initialized ||
      typeof window === 'undefined'
    ) {
      return;
    }

    // Initialiser la file de commandes Matomo
    window._paq = window._paq ?? [];
    // Domaine dynamique (jamais en dur) — s'adapte au domaine de prod réel
    // (DuckDNS ou futur domaine payant), voir ADR-017.
    this.push(['setCookieDomain', `*.${window.location.hostname}`]);
    this.push(['setTrackerUrl', `${this.cfg.url}matomo.php`]);
    this.push(['setSiteId', String(this.cfg.siteId)]);
    this.push(['enableLinkTracking']);
    this.push(['enableHeartBeatTimer', 30]); // session active toutes les 30s

    // Charger le script Matomo de manière asynchrone
    this._loadScript();

    // Tracking automatique des changements de route Angular
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.push(['setCustomUrl', event.urlAfterRedirects]);
        this.push(['setDocumentTitle', document.title]);
        this.push(['trackPageView']);
      });

    this.initialized = true;
  }

  // ─── Identification utilisateur ──────────────────────────────────────────

  /** Lie tous les events suivants à un merchant (appelé après login) */
  setUserId(merchantId: string): void {
    this.push(['setUserId', merchantId]);
    this.push(['trackPageView']); // re-track la page courante avec le userId
  }

  /** Réinitialise l'identité (appelé à logout) */
  resetUserId(): void {
    this.push(['resetUserId']);
    this.push(['deleteCookies']);
  }

  // ─── Page views ──────────────────────────────────────────────────────────

  trackPageView(url?: string, title?: string): void {
    if (url) this.push(['setCustomUrl', url]);
    if (title) this.push(['setDocumentTitle', title]);
    this.push(['trackPageView']);
  }

  // ─── Events métier ───────────────────────────────────────────────────────

  /** Inscription réussie */
  trackRegister(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'inscription');
  }

  /** Connexion réussie */
  trackLogin(merchantType?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'connexion', merchantType);
  }

  /** Déconnexion */
  trackLogout(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'deconnexion');
  }

  /** Vente enregistrée (kiosk ou manuelle) */
  trackVente(
    montantTotal: number,
    source: 'kiosk' | 'manual',
    nbProduits?: number,
  ): void {
    this.trackEvent(
      MATOMO_CATEGORIES.VENTE,
      'enregistrement',
      source,
      montantTotal,
    );
    if (nbProduits !== undefined) {
      this.trackEvent(
        MATOMO_CATEGORIES.VENTE,
        'nb_produits',
        source,
        nbProduits,
      );
    }
  }

  /** Vente supprimée */
  trackVenteDelete(): void {
    this.trackEvent(MATOMO_CATEGORIES.VENTE, 'suppression');
  }

  /** Produit ajouté au catalogue */
  trackProduitAjoute(categorie?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.PRODUIT, 'ajout', categorie);
  }

  /** Produit modifié */
  trackProduitModifie(): void {
    this.trackEvent(MATOMO_CATEGORIES.PRODUIT, 'modification');
  }

  /** Onboarding : étape complétée */
  trackOnboardingStep(etape: string): void {
    this.trackEvent(MATOMO_CATEGORIES.ONBOARDING, 'etape_completee', etape);
  }

  /** Onboarding terminé */
  trackOnboardingComplete(merchantType: string): void {
    this.trackEvent(MATOMO_CATEGORIES.ONBOARDING, 'complete', merchantType);
  }

  /** Event de marché créé */
  trackEventCree(eventType: string): void {
    this.trackEvent(MATOMO_CATEGORIES.EVENT, 'creation', eventType);
  }

  /** Event de marché validé (ventes saisies après l'event) */
  trackEventValide(): void {
    this.trackEvent(MATOMO_CATEGORIES.EVENT, 'validation');
  }

  /** Abonnement souscrit */
  trackAbonnement(plan: string, montant?: number): void {
    this.trackEvent(
      MATOMO_CATEGORIES.ABONNEMENT,
      'souscription',
      plan,
      montant,
    );
  }

  /** Paiement effectué (côté client, les paiements backend sont trackés côté serveur) */
  trackPaiement(statut: 'succes' | 'echec', plan?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.ABONNEMENT, `paiement_${statut}`, plan);
  }

  /** Changement de plan (upgrade/downgrade) */
  trackAbonnementChange(targetPlan: string): void {
    this.trackEvent(
      MATOMO_CATEGORIES.ABONNEMENT,
      'changement_plan',
      targetPlan,
    );
  }

  /** Annulation d'abonnement */
  trackAbonnementAnnule(raison?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.ABONNEMENT, 'annulation', raison);
  }

  // ─── Produits ─────────────────────────────────────────────────────────────

  /** Produit supprimé du catalogue */
  trackProduitSupprime(nom?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.PRODUIT, 'suppression', nom);
  }

  // ─── Services ─────────────────────────────────────────────────────────────

  /** Service créé */
  trackServiceCree(nom?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.SERVICE, 'creation', nom);
  }

  /** Service démarré */
  trackServiceDemarre(nom?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.SERVICE, 'demarrage', nom);
  }

  /** Service terminé */
  trackServiceTermine(nom?: string): void {
    this.trackEvent(MATOMO_CATEGORIES.SERVICE, 'termine', nom);
  }

  // ─── Paramètres & compte ──────────────────────────────────────────────────

  /** Paramètres sauvegardés */
  trackParametresModifies(): void {
    this.trackEvent(MATOMO_CATEGORIES.PARAMETRES, 'sauvegarde');
  }

  /** Mot de passe changé (depuis les paramètres) */
  trackMotDePasseChange(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'mot_de_passe_change');
  }

  /** Compte supprimé */
  trackCompteSupprime(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'compte_supprime');
  }

  /** Mot de passe oublié demandé */
  trackMotDePasseOublie(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'mot_de_passe_oublie');
  }

  /** Mot de passe réinitialisé avec succès */
  trackMotDePasseReset(): void {
    this.trackEvent(MATOMO_CATEGORIES.AUTH, 'mot_de_passe_reset');
  }

  // ─── Ventes ───────────────────────────────────────────────────────────────

  /** Soumission de ventes journalières */
  trackVenteSoumise(nbServices: number): void {
    this.trackEvent(
      MATOMO_CATEGORIES.VENTE,
      'soumission',
      undefined,
      nbServices,
    );
  }

  // ─── API bas niveau ───────────────────────────────────────────────────────

  /**
   * Envoi générique d'un event Matomo.
   * @param category  Catégorie (ex: 'Vente')
   * @param action    Action (ex: 'enregistrement')
   * @param name      Label optionnel (ex: 'kiosk')
   * @param value     Valeur numérique optionnelle (ex: montant)
   */
  trackEvent(
    category: string,
    action: string,
    name?: string,
    value?: number,
  ): void {
    const cmd: unknown[] = ['trackEvent', category, action];
    if (name !== undefined) cmd.push(name);
    if (value !== undefined) cmd.push(value);
    this.push(cmd);
  }

  /** Envoi d'une commande brute dans la file Matomo */
  push(cmd: unknown[]): void {
    if (!this.cfg.enabled || typeof window === 'undefined') return;
    window._paq = window._paq ?? [];
    window._paq.push(cmd);
  }

  // ─── Chargement du script ─────────────────────────────────────────────────

  private _loadScript(): void {
    if (document.querySelector(`script[src*="matomo.js"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `${this.cfg.url}matomo.js`;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  }
}
